import { useState, useEffect, useRef } from "react"
import { AbstractVideoController, VideoControllerEvents } from "../AbstractVideoController"
import {
    GetOrCreateVideoControllerParameters,
    VideoControllerFactory,
} from "../VideoControllerFactory"
import {
    useVideoControllerForFlight,
    useGuardedVideoControllerForFlight,
    GuardClass,
} from "./useController"

function useControllerEventBinding<TController extends AbstractVideoController, TValue>({
    controller,
    defaultValue,
    func,
    events,
}: {
    controller: TController | undefined
    defaultValue: TValue
    func: (c: TController) => TValue
    events: VideoControllerEvents[]
}) {
    const [val, setVal] = useState<TValue>(defaultValue)
    const funcRef = useRef(func)
    useEffect(() => {
        funcRef.current = func
    }, [func])
    const eventsKey = events.join(",")
    useEffect(() => {
        if (!controller) return
        const events = eventsKey.split(",") as VideoControllerEvents[]
        const update = () => {
            setVal(funcRef.current(controller))
        }
        events.forEach((event) => controller.on(event, update))
        update()
        return () => {
            events.forEach((event) => controller.off(event, update))
        }
    }, [controller, eventsKey])
    return val
}
export function useVideoControllerEventBinding<TValue>({
    flightId,
    options,
    defaultValue,
    func,
    events,
}: GetOrCreateVideoControllerParameters & {
    defaultValue: TValue
    func: (controller: AbstractVideoController) => TValue
    events: VideoControllerEvents[]
}) {
    const controller = useVideoControllerForFlight({ flightId, options })
    return useControllerEventBinding({
        controller,
        defaultValue,
        func,
        events,
    })
}
export function useGuardedVideoControllerEventBinding<
    TController extends AbstractVideoController,
    TValue,
>({
    flightId,
    options,
    guardClass,
    defaultValue,
    func,
    events,
}: GetOrCreateVideoControllerParameters & {
    guardClass: GuardClass<TController>
    defaultValue: TValue
    func: (controller: TController) => TValue
    events: VideoControllerEvents[]
}) {
    const controller = useGuardedVideoControllerForFlight({ flightId, options, guardClass })
    return useControllerEventBinding({
        controller,
        defaultValue,
        func,
        events,
    })
}

export function useVideoControllerMultipleFlightEventBinding<TValue>({
    flightIds,
    options,
    func,
    events,
}: {
    flightIds: string[]
    options: GetOrCreateVideoControllerParameters["options"]
    func: (controller: AbstractVideoController) => TValue
    events: VideoControllerEvents[]
}) {
    const [vals, setVals] = useState<Record<string, TValue>>({})
    const funcRef = useRef(func)
    useEffect(() => {
        funcRef.current = func
    }, [func])
    const optionsRef = useRef(options)
    useEffect(() => {
        optionsRef.current = options
    }, [options])
    const eventsKey = events.join(",")
    const flightIdsKey = flightIds.join(",")
    const controllersRef = useRef<
        Record<
            string,
            { controller: AbstractVideoController; cleanup: () => void; boundEventsKey: string }
        >
    >({})
    useEffect(() => {
        const events = eventsKey.split(",") as VideoControllerEvents[]
        const flightIds = flightIdsKey.split(",")
        const activeIds = new Set(flightIds)
        const map = controllersRef.current
        Object.entries(map).forEach(([key, value]) => {
            if (!activeIds.has(key)) {
                value.cleanup()
                delete map[key]
                setVals((prev) => {
                    const next = { ...prev }
                    delete next[key]
                    return next
                })
            }
        })
        flightIds.forEach(async (flightId) => {
            if (!flightId) return
            try {
                if (map[flightId]) {
                    if (map[flightId].boundEventsKey === eventsKey) return
                    map[flightId].cleanup()
                }
                const controller =
                    map[flightId]?.controller ||
                    (await VideoControllerFactory.getOrCreateControllerForFlight({
                        flightId,
                        options: optionsRef.current,
                    }))
                if (map[flightId]) return
                if (!controller) throw new Error()
                if (!activeIds.has(flightId)) return
                const set = () => {
                    setVals((prev) => ({
                        ...prev,
                        [flightId]: funcRef.current(controller),
                    }))
                }
                for (const event of events) {
                    controller.on(event, set)
                }
                set()
                map[flightId] = {
                    controller,
                    cleanup: () => {
                        for (const event of events) {
                            controller.off(event, set)
                        }
                    },
                    boundEventsKey: eventsKey,
                }
            } catch (error) {
                console.error(`Failed to init controller for ${flightId}`, error)
            }
        })
    }, [eventsKey, flightIdsKey])
    useEffect(() => {
        const refCurrent = controllersRef.current
        return () => {
            Object.entries(refCurrent).forEach(([key, value]) => {
                value.cleanup()
                delete refCurrent[key]
            })
        }
    }, [])
    return { vals, key: JSON.stringify(vals) }
}
